import React, { useState, useEffect, useRef } from 'react';
import image1 from '../../assets/images/image1.png';
import image2 from '../../assets/images/image2.png';
import { Icon } from '@iconify/react';
import { inspectionService, Checklist, Inspection } from '../../services/inspection.service';
import { format } from 'date-fns';

interface ViewSummaryProps {
    onClose: () => void;
    inspection?: Inspection | null;
    onReturnToLog?: () => void;
}

export default function ViewSummary({ onClose, inspection, onReturnToLog }: ViewSummaryProps) {
    const [checklists, setChecklists] = useState<Checklist[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    // Use ref to track if data has been loaded to prevent re-fetching
    const dataLoadedRef = useRef(false);
    const inspectionIdRef = useRef<number | null>(null);

    useEffect(() => {
        // Only fetch data if inspection ID has changed or data hasn't been loaded yet
        if (inspection?.id && (inspectionIdRef.current !== inspection.id || !dataLoadedRef.current)) {
            inspectionIdRef.current = inspection.id;
            dataLoadedRef.current = true;
            fetchChecklists();
        }
    }, [inspection?.id]); // Only depend on inspection ID, not the entire inspection object

    const fetchChecklists = async () => {
        if (!inspection?.id) return;

        try {
            setLoading(true);
            setError(null);
            const data = await inspectionService.getChecklistsByInspection(inspection.id);
            setChecklists(data);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to fetch checklist data');
            console.error('Failed to fetch checklists:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        // Reset the data loaded flag so modal can work for future inspections
        dataLoadedRef.current = false;
        inspectionIdRef.current = null;
        onClose();
    };

    const formatAlertStatus = (alertStatus: string) => {
        switch (alertStatus) {
            case 'sent':
                return 'Yes';
            case 'failed':
                return 'Failed';
            case 'pending':
            default:
                return '--';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'passed':
                return 'text-green font-[Geist-semibold]';
            case 'acceptable':
                return 'text-orange-500 font-[Geist-semibold]';
            case 'failed':
                return 'text-[#F60000] font-[Geist-semibold]';
            default:
                return 'text-gray-500';
        }
    };

    const getDisplayStatus = (status: string) => {
        switch (status) {
            case 'passed':
                return 'Fixed';
            case 'acceptable':
                return 'Acceptable';
            case 'failed':
                return 'Not Fixed';
            default:
                return status.charAt(0).toUpperCase() + status.slice(1);
        }
    };

    const formatTimestamp = inspection ? format(new Date(inspection.createdAt), 'MMMM dd, yyyy, hh:mm a') : '--';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center w-full p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-3">
                    <h2 className="text-black-1 text-xl font-[Geist-Bold]">Event Log Details</h2>
                    <button 
                        onClick={handleClose} 
                        className="w-8 h-8 rounded-full bg-gray-1 hover:bg-gray-2 flex items-center justify-center transition-colors"
                    >
                        <Icon icon="material-symbols:close" className="text-gray-2 text-lg" />
                    </button>
                </div>

                {/* Info Cards */}
                <div className="px-6 py-4 bg-gray-6">
                    <div className="flex flex-wrap gap-4">
                        <div className="flex items-center gap-2">
                            <span className="text-gray-2 text-sm font-[Geist-Medium]">Inspection ID:</span>
                            <span className="px-3 py-1 bg-white border border-gray-3 rounded-lg text-black-1 text-sm font-[Geist-SemiBold]">
                                {inspection?.id || '--'}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-gray-2 text-sm font-[Geist-Medium]">Timestamp:</span>
                            <span className="px-3 py-1 bg-white border border-gray-3 rounded-lg text-black-1 text-sm font-[Geist-SemiBold]">
                                {formatTimestamp}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="flex items-center justify-center h-64">
                            <div className="flex items-center gap-2 text-gray-2">
                                <Icon icon="eos-icons:loading" className="text-lg animate-spin" />
                                Loading inspection details...
                            </div>
                        </div>
                    ) : error ? (
                        <div className="flex items-center justify-center h-64">
                            <div className="text-center">
                                <div className="text-red-500 mb-2">{error}</div>
                                <button 
                                    onClick={fetchChecklists} 
                                    className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-[Geist-Medium] transition-colors"
                                >
                                    Retry
                                </button>
                            </div>
                        </div>
                    ) : !inspection ? (
                        <div className="flex items-center justify-center h-64">
                            <div className="text-gray-2">No inspection data available</div>
                        </div>
                    ) : (
                        /* Event Summary */
                        <div className="p-6 space-y-6">
                            <h3 className="text-lg font-[Geist-Bold] text-black-1 mb-4">Event Summary</h3>
                            <div className="bg-white border border-gray-3 rounded-xl overflow-hidden">
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-gray-6">
                                            <th className="px-4 py-3 text-left text-gray font-[Geist-Medium] text-sm">Trailer ID</th>
                                            <th className="px-4 py-3 text-center text-gray font-[Geist-Medium] text-sm">Location</th>
                                            <th className="px-4 py-3 text-center text-gray font-[Geist-Medium] text-sm">Event</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr className="border-t border-gray-3">
                                            <td className="px-4 py-3 text-black-1 font-[Geist-Medium] text-sm">{inspection.trailerId}</td>
                                            <td className="px-4 py-3 text-center text-gray-2 font-[Geist-Medium] text-sm">--</td>
                                            <td className="px-4 py-3 text-center">
                                                <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-[Geist-SemiBold] ${
                                                    inspection.status === 'completed'
                                                        ? 'bg-green-1 text-green border border-green-200'
                                                        : inspection.status === 'failed'
                                                            ? 'bg-red-50 text-red border border-red-200'
                                                            : inspection.status === 'cancelled'
                                                                ? 'bg-gray-1 text-gray-2 border border-gray-3'
                                                                : 'bg-red-50 text-red border border-red-200'
                                                }`}>
                                                    {inspection.status === 'completed'
                                                        ? 'Inspection Completed'
                                                        : inspection.status === 'failed'
                                                            ? 'Inspection Failed'
                                                            : inspection.status === 'cancelled'
                                                                ? 'Inspection Cancelled'
                                                                : 'Critical Event'}
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* Inspection Checklist */}
                            <h3 className="text-lg font-[Geist-Bold] text-black-1 mb-4">Inspection Checklist</h3>
                            <div className="bg-white border border-gray-3 rounded-xl overflow-hidden">
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-gray-6">
                                            <th className="px-4 py-3 text-left text-gray font-[Geist-Medium] text-sm">Inspection Checklist</th>
                                            <th className="px-4 py-3 text-center text-gray font-[Geist-Medium] text-sm">Status</th>
                                            <th className="px-4 py-3 text-center text-gray font-[Geist-Medium] text-sm">Alert Sent</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {checklists.map((checklist, index) => (
                                            <tr key={checklist.id} className={`border-t border-gray-3 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-6'}`}>
                                                <td className="px-4 py-3 text-black-1 font-[Geist-Medium] text-sm">
                                                    {checklist.name}
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-[Geist-SemiBold] ${
                                                        checklist.status === 'passed'
                                                            ? 'bg-green-1 text-green border border-green-200'
                                                            : checklist.status === 'acceptable'
                                                                ? 'bg-yellow-1 text-yellow border border-yellow-200'
                                                                : checklist.status === 'failed'
                                                                    ? 'bg-red-50 text-red border border-red-200'
                                                                    : 'bg-gray-1 text-gray-2 border border-gray-3'
                                                    }`}>
                                                        {getDisplayStatus(checklist.status)}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-[Geist-Medium] ${
                                                        checklist.alertStatus === 'sent'
                                                            ? 'bg-green-1 text-green'
                                                            : checklist.alertStatus === 'failed'
                                                                ? 'bg-red-50 text-red'
                                                                : 'bg-gray-1 text-gray-2'
                                                    }`}>
                                                        {formatAlertStatus(checklist.alertStatus)}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Images */}
                            <h3 className="text-lg font-[Geist-Bold] text-black-1 mb-4">Images</h3>
                            <div className="bg-white border border-gray-3 rounded-xl p-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {(() => {
                                        // Collect all fault photos from all failed checklists
                                        const allFaultPhotos: string[] = [];
                                        const allPhotos: string[] = [];

                                        checklists.forEach(checklist => {
                                            // Show fault photos from any checklist that has them (regardless of current status)
                                            if (checklist.faultPhotosUrls && checklist.faultPhotosUrls.length > 0) {
                                                allFaultPhotos.push(...checklist.faultPhotosUrls);
                                            } else if (checklist.photoUrls && checklist.photoUrls.length > 0) {
                                                allPhotos.push(...checklist.photoUrls);
                                            }
                                        });

                                        // Use fault photos first, then regular photos
                                        const photosToShow = allFaultPhotos.length > 0 ? allFaultPhotos : [];

                                        if (photosToShow.length > 0) {
                                            return photosToShow.map((photoUrl: string, index: number) => (
                                                <div key={index} className="rounded-lg overflow-hidden border border-gray-3">
                                                    <img
                                                        src={photoUrl}
                                                        alt={`Inspection image ${index + 1}`}
                                                        className="max-w-full h-auto"
                                                        onError={(e) => {
                                                            e.currentTarget.src = index % 2 === 0 ? image1 : image2;
                                                        }}
                                                    />
                                                </div>
                                            ));
                                        } else {
                                            return (
                                                <div className="col-span-2 text-center py-8">
                                                    <Icon icon="material-symbols:image-outline" className="text-4xl text-gray-2 mx-auto mb-2" />
                                                    <p className="text-gray-2 text-sm font-[Geist-Medium]">No images available</p>
                                                </div>
                                            );
                                        }
                                    })()}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-3 bg-gray-6">
                    <div className="flex justify-end">
                        <button
                            onClick={() => {
                                handleClose();
                                if (onReturnToLog) {
                                    onReturnToLog();
                                }
                            }}
                            className="bg-blue hover:bg-blue-1 text-white px-6 py-2 rounded-lg text-sm font-[Geist-Medium] transition-colors"
                        >
                            Return to Log
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};